import React, {useEffect, useState} from 'react';
import {Box, Button, Checkbox, ListItemText, MenuItem, TextField} from '@mui/material';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import {FormBuilderComponentTypes} from './ComponentList';

export const ADDRESS_SHOW_FIELD_WHEN = {
    UK: 'UK',
    NONE_UK: 'None-UK',
};

export const getAddressShowFieldWhenValue = (addressValue) => {
    const countryCode = addressValue?.countryCode;

    if (!countryCode) {
        return undefined;
    }

    return countryCode === 'GBR'
        ? ADDRESS_SHOW_FIELD_WHEN.UK
        : ADDRESS_SHOW_FIELD_WHEN.NONE_UK;
};

// A school may pick more than one answer that reveals the question. One answer is stored on
// its own, as it always was, so a form saved before this still reads the same.
export const targetStatesOf = relates => {
    const targetState = relates?.targetState;

    if (targetState === undefined || targetState === null) {
        return [];
    }

    return Array.isArray(targetState) ? targetState : [targetState];
};

export const relatesToOption = (relates, optionId) => targetStatesOf(relates).includes(optionId);

const matchesOneTargetState = (relatedField, relatedValue, targetState) => {
    if (relatedField?.type === FormBuilderComponentTypes.Address) {
        return getAddressShowFieldWhenValue(relatedValue) === targetState;
    }

    if (Array.isArray(relatedValue)) {
        return relatedValue.includes(targetState);
    }

    return relatedValue === targetState || (targetState === false && relatedValue === undefined);
};

// A school chooses how the answers are read. A rule saved before this carries no condition, so it
// reads as Equals any of and the form behaves as it did.
export const SHOW_FIELD_CONDITIONS = {
    anyOf: 'anyOf',
    isNot: 'isNot',
    allOf: 'allOf',
    only: 'only'
};

export const SHOW_FIELD_CONDITION_LABELS = {
    [SHOW_FIELD_CONDITIONS.anyOf]: 'Equals any of',
    [SHOW_FIELD_CONDITIONS.isNot]: 'Is not',
    [SHOW_FIELD_CONDITIONS.allOf]: 'All of',
    [SHOW_FIELD_CONDITIONS.only]: 'Only'
};

export const conditionOf = relates => relates?.condition ?? SHOW_FIELD_CONDITIONS.anyOf;

const answersGiven = (relatedField, relatedValue) => {
    if (relatedField?.type === FormBuilderComponentTypes.Address) {
        const value = getAddressShowFieldWhenValue(relatedValue);

        return value === undefined ? [] : [value];
    }

    if (Array.isArray(relatedValue)) {
        return relatedValue;
    }

    if (relatedValue === undefined || relatedValue === null || relatedValue === '') {
        return [];
    }

    return [relatedValue];
};

export const matchesRelatesTargetState = (relatedField, relatedValue, targetState, condition) => {
    const wanted = Array.isArray(targetState) ? targetState : [targetState];
    const matches = one => matchesOneTargetState(relatedField, relatedValue, one);

    switch (condition ?? SHOW_FIELD_CONDITIONS.anyOf) {
        case SHOW_FIELD_CONDITIONS.isNot:
            return answersGiven(relatedField, relatedValue).length > 0 && !wanted.some(matches);
        case SHOW_FIELD_CONDITIONS.allOf:
            return wanted.every(matches);
        case SHOW_FIELD_CONDITIONS.only:
            return wanted.every(matches) &&
                answersGiven(relatedField, relatedValue).every(answer => wanted.includes(answer));
        default:
            return wanted.some(matches);
    }
};

export const determinePossibleOperations = (component, options = {}) => {
    const { checkboxOnlyChecked = false } = options;

    switch (component?.type) {
        case 'checkboxQuestion':
            if (checkboxOnlyChecked) {
                return [{value: true, title: 'Checked'}];
            }
            return [{value: true, title: 'Checked'}, {value: false, title: 'Not Checked'}]
        case 'dropdown':
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case 'singleChoice':
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case 'multipleChoice':
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case FormBuilderComponentTypes.OfferStatusChoice:
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case FormBuilderComponentTypes.LookupDropdown:
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case FormBuilderComponentTypes.IntakeChoice:
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case FormBuilderComponentTypes.BoarderStatusChoice:
            return component.options?.map(o => {
                return {value: o.id, title: o.description}
            })
        case FormBuilderComponentTypes.Address:
            return [
                {value: ADDRESS_SHOW_FIELD_WHEN.UK, title: 'UK'},
                {value: ADDRESS_SHOW_FIELD_WHEN.NONE_UK, title: 'None-UK'},
            ]
        default:
            return []
    }
}

const ShowFieldWhen = ({component, handleShowFieldWhen, availableComponents}) => {
    const [selectedComponent, setSelectedComponent] = useState(undefined);
    const [targetStates, setTargetStates] = useState([]);
    const [condition, setCondition] = useState(SHOW_FIELD_CONDITIONS.anyOf);

    useEffect(() => {
        if (component) {
            setSelectedComponent(availableComponents.find(e => e.id === component.relates?.relatesTo));
            setTargetStates(targetStatesOf(component.relates));
            setCondition(conditionOf(component.relates));
        }
    }, [component]);

    const chosenAnswers = () => targetStates.length === 1 ? targetStates[0] : targetStates;

    return (
        <>
            <Typography>Question:</Typography>
            <TextField
                key={selectedComponent?.id}
                select
                value={selectedComponent}
                fullWidth
                onChange={e => {
                    setSelectedComponent(e.target.value);
                    setTargetStates([]);
                }}
            >
                {availableComponents.map(option => <MenuItem key={option.id} value={option}>
                    {option.label} - {option.description}
                </MenuItem>)}
            </TextField>
            <Typography pt={1}>Condition:</Typography>
            <TextField
                select
                value={condition}
                fullWidth
                disabled={!selectedComponent}
                onChange={e => setCondition(e.target.value)}
            >
                {Object.values(SHOW_FIELD_CONDITIONS).map(value =>
                    <MenuItem key={value} value={value}>
                        {SHOW_FIELD_CONDITION_LABELS[value]}
                    </MenuItem>)}
            </TextField>
            <Typography pt={1}>{SHOW_FIELD_CONDITION_LABELS[condition]}</Typography>
            <TextField
                select
                value={targetStates}
                fullWidth
                disabled={!selectedComponent}
                SelectProps={{
                    multiple: true,
                    renderValue: chosen => (determinePossibleOperations(selectedComponent) || [])
                        .filter(operation => chosen.includes(operation.value))
                        .map(operation => operation.title)
                        .join(', ')
                }}
                onChange={e => setTargetStates(
                    Array.isArray(e.target.value) ? e.target.value : [e.target.value])}
            >
                {selectedComponent && determinePossibleOperations(selectedComponent)?.map(operation =>
                    <MenuItem key={String(operation.value)} value={operation.value}>
                        <Checkbox size="small" checked={targetStates.includes(operation.value)}/>
                        <ListItemText primary={operation.title}/>
                    </MenuItem>)}
            </TextField>

            <Box display={'flex'} justifyContent={'end'} pt={2} gap={1}>
                <Button onClick={() => handleShowFieldWhen(component.id, null, null, null)}>
                    Clear
                </Button>
                <Button variant={'contained'}
                        disabled={!selectedComponent || targetStates.length === 0}
                        onClick={() => handleShowFieldWhen(
                            component.id, selectedComponent.id, chosenAnswers(), condition)}>
                    Save
                </Button>
            </Box>

        </>
    )
}

ShowFieldWhen.propTypes = {
    component: PropTypes.object,
    handleShowFieldWhen: PropTypes.func,
    availableComponents: PropTypes.array,
};

export default ShowFieldWhen;